import React from 'react';
import { Link } from 'react-router-dom';
import { FiCopy } from 'react-icons/fi';
import toast from 'react-hot-toast';
import Footer from '../components/Footer';
import andyPhoto from '../img/profile/small_andy-bluebg-square.png';

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'andy@andycary.com';

// Feature flags: control visibility of Home page sections
const FEATURE_FLAGS = {
  showPragmaticAI: true,
  showBuiltFromScratch: true,
  showProof: true,
  showTheStack: true,
  showWriting: false,
};

// The Stack: rows render in order; `highlight` uses the accent chip style
const STACK = [
  {
    label: 'AI & Agents',
    highlight: true,
    items: [
      'LLM integration & prompt engineering',
      'Model Context Protocol (MCP)',
      'Human-in-the-loop agent design',
      'Workflow automation',
      'Oracle Cloud AI (certified)',
    ],
  },
  {
    label: 'Languages & Frameworks',
    items: ['TypeScript / JavaScript', 'Java', 'Python', 'SQL', 'React', 'Node.js', 'Spring Boot', 'GraphQL', 'Angular'],
  },
  {
    label: 'Cloud & Infrastructure',
    items: ['AWS', 'GCP', 'Azure', 'Oracle Cloud', 'Docker', 'Microservices'],
  },
  {
    label: 'Testing & Quality',
    items: ['Cypress', 'Selenium', 'JUnit & Mockito', 'SonarQube', 'TDD', 'CI/CD (Jenkins, GitLab CI)'],
  },
];

const handleCopyEmail = () => {
  navigator.clipboard.writeText(CONTACT_EMAIL).then(() => {
    toast.success('Email copied to clipboard');
  });
};

const Home = () => {
  return (
    <div className="min-h-screen bg-background-dark text-slate-100 font-display">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-accent/10 bg-background-dark/80 backdrop-blur-md">
        <div className="flex items-center justify-between p-4 max-w-screen-md mx-auto">
          <h2 className="text-slate-100 text-lg font-bold leading-tight tracking-tight">
            Andy Cary
          </h2>
          <button
            type="button"
            onClick={handleCopyEmail}
            className="flex items-center gap-2 text-accent text-sm font-bold tracking-wide uppercase hover:bg-accent/10 px-3 py-1 rounded transition-colors"
          >
            <FiCopy className="text-sm" />
            <span>Copy Email</span>
          </button>
        </div>
      </header>

      <main className="max-w-screen-md mx-auto">
        {/* Hero */}
        <section className="relative overflow-hidden px-4 pt-12 pb-20 border-b border-accent/10 code-bg">
          <div className="relative z-10 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                <span className="absolute -inset-1.5 rounded-full bg-accent/20 blur-md" aria-hidden="true" />
                <img
                  src={andyPhoto}
                  alt="Andy Cary smiling"
                  className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-accent/40 shadow-lg"
                />
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-accent/10 border border-accent/20 w-fit">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                </span>
                <span className="font-mono text-[10px] text-accent uppercase tracking-widest">
                  Available for selective AI builds
                </span>
              </div>
            </div>
            <h1 className="text-slate-100 text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              Engineering AI for the Real World.
            </h1>
            <p className="text-slate-400 text-lg font-normal leading-relaxed max-w-lg">
              Hi, I'm Andy Cary — a Senior Software Engineer turning emerging AI
              capabilities into
              {' '}
              <span className="text-slate-100">reliable, production-ready systems.</span>
            </p>
            <p className="text-slate-400 text-base leading-relaxed max-w-lg">
              From building enterprise software for The Home Depot's Pro contractors
              to developing AI agents for the trades, my focus remains the same:
              {' '}
              <span className="text-accent font-semibold">solving real problems and building technology people can trust.</span>
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/portfolio"
                className="flex min-w-[140px] cursor-pointer items-center justify-center rounded bg-accent text-background-dark px-6 py-3 text-sm font-bold tracking-wide transition-transform active:scale-95"
              >
                View Projects
              </Link>
              <Link
                to="/blog"
                className="flex min-w-[140px] cursor-pointer items-center justify-center rounded border border-accent/30 text-accent px-6 py-3 text-sm font-bold tracking-wide hover:bg-accent/5"
              >
                Read the Blog
              </Link>
            </div>
          </div>
          <div className="absolute -right-12 -bottom-12 opacity-5 pointer-events-none font-mono text-xs overflow-hidden select-none">
            <pre>
              {`while (system.optimizing) {
  evaluate(model.output);
  backfill(vector_db);
  latency.monitor();
  human_in_the_loop.verify();
}`}
            </pre>
          </div>
        </section>

        {/* Pragmatic AI */}
        {FEATURE_FLAGS.showPragmaticAI && (
        <section className="px-4 py-16 border-b border-accent/10">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <h2 className="text-slate-100 text-2xl font-bold tracking-tight">
                Pragmatic AI
              </h2>
              <div className="h-px flex-1 bg-accent/10" />
            </div>
            <div className="grid gap-6">
              <p className="text-slate-400 text-base leading-relaxed">
                The gap between a Jupyter notebook demo and a production system
                is wide. I bridge that gap by prioritizing{' '}
                <span className="text-accent font-bold">reliability over hype.</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded border border-accent/10 bg-accent/5">
                  <h3 className="font-mono text-xs text-accent uppercase mb-2">
                    Engineering Foundation
                  </h3>
                  <p className="text-sm text-slate-300">
                    Full-stack systems in React, TypeScript, Node.js, and
                    Java/Spring Boot microservices.
                  </p>
                </div>
                <div className="p-4 rounded border border-accent/10 bg-accent/5">
                  <h3 className="font-mono text-xs text-accent uppercase mb-2">
                    Operational Excellence
                  </h3>
                  <p className="text-sm text-slate-300">
                    Test coverage, CI/CD discipline, and cloud-native delivery
                    at enterprise scale.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Built from Scratch */}
        {FEATURE_FLAGS.showBuiltFromScratch && (
        <section className="px-4 py-16 border-b border-accent/10">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-4">
              <h2 className="text-slate-100 text-2xl font-bold tracking-tight">
                Built from Scratch
              </h2>
              <div className="h-px flex-1 bg-accent/10" />
            </div>
            <p className="text-slate-400 text-base leading-relaxed -mt-2">
              Self-initiated AI systems: no ticket, no team, real users.
            </p>
            <div className="flex flex-col gap-12">
              <div className="group relative">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-slate-100 text-xl font-bold">
                      Axiom Standard
                    </h3>
                    <span className="font-mono text-[10px] border border-accent/30 text-accent px-2 py-0.5 rounded shrink-0">
                      METAMOTION LLC · FOUNDER
                    </span>
                  </div>
                  <div className="space-y-4 font-mono text-sm">
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">PROBLEM:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Small HVAC, plumbing, and electrical shops are buried in follow-ups and back-office admin, but no owner will let an AI act on their customers unsupervised.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">INTERVENE:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Designed an agent platform around a hard trust boundary: agents propose, owners approve before anything executes. A three-path approval flow and a Ref-ID receipt on every action make each decision traceable.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">RESULT:</span>
                      <span className="text-slate-100 leading-relaxed">
                        Running with two live pilot tenants. The product is the trust architecture, not the model.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="group relative">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="text-slate-100 text-xl font-bold">
                      Atlas Crest MCP Gateway
                    </h3>
                    <span className="font-mono text-[10px] border border-accent/30 text-accent px-2 py-0.5 rounded shrink-0">
                      SELF-HOSTED · PYTHON
                    </span>
                  </div>
                  <div className="space-y-4 font-mono text-sm">
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">PROBLEM:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Every AI client needed its own connection and credentials for each tool server, fragmenting access to GitHub and Linear.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">INTERVENE:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Built a Python gateway that aggregates upstream MCP servers into one namespaced endpoint (79 tools) behind a five-layer middleware stack on a private Tailscale network.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">RESULT:</span>
                      <span className="text-slate-100 leading-relaxed">
                        Caught that wildcard CORS plus unauthenticated access exposed more than documented, closed it with bearer-token auth, and wrote it up. Now powers my daily and weekly Linear briefs.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Proof */}
        {FEATURE_FLAGS.showProof && (
        <section className="px-4 py-16 border-b border-accent/10">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-4">
              <h2 className="text-slate-100 text-2xl font-bold tracking-tight">
                Enterprise Proof
              </h2>
              <div className="h-px flex-1 bg-accent/10" />
            </div>
            <div className="flex flex-col gap-12">
              <div className="group relative">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-slate-100 text-xl font-bold">
                      Pro Order Up Platform
                    </h3>
                    <span className="font-mono text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                      HOME DEPOT · 2025–PRESENT
                    </span>
                  </div>
                  <div className="space-y-4 font-mono text-sm">
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">PROBLEM:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Manual rollout and reconciliation work across the
                        Pro Order Up platform was slowing delivery and
                        introducing risk.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">INTERVENE:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Replaced manual steps with reusable automation and
                        safer defaults, pairing feature work with rigorous
                        test coverage and hotfix discipline across frontend,
                        backend, and architecture decisions.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">RESULT:</span>
                      <span className="text-slate-100 leading-relaxed">
                        Expanded scope into platform integration and
                        system-level ownership, while mentoring an intern
                        from onboarding to executive-ready communication.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="group relative">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-slate-100 text-xl font-bold">
                      Order Visibility (ProAssist)
                    </h3>
                    <span className="font-mono text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                      HOME DEPOT · 2023–2025
                    </span>
                  </div>
                  <div className="space-y-4 font-mono text-sm">
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">PROBLEM:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Inside Sales Support Reps had no direct visibility
                        into Deliveries and Special Orders shipping status
                        inside the Order Management system.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">INTERVENE:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Built nested table views and shipping-detail displays
                        in the ProAssist (QC2) app using Node.js, React,
                        TypeScript, and GraphQL, working directly with
                        product and engineering leadership.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">RESULT:</span>
                      <span className="text-slate-100 leading-relaxed">
                        Shipped across multiple production releases, giving
                        reps order status inside the tool they already use
                        daily.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="group relative">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-slate-100 text-xl font-bold">
                      FEMA Data Management Platform
                    </h3>
                    <span className="font-mono text-[10px] border border-accent/30 text-accent px-2 py-0.5 rounded">
                      REI SYSTEMS · 2022–2023
                    </span>
                  </div>
                  <div className="space-y-4 font-mono text-sm">
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">PROBLEM:</span>
                      <span className="text-slate-400 leading-relaxed">
                        A federal data platform pilot needed auditable user
                        activity logging and reliable quality gates across
                        nine Java microservices to meet compliance
                        requirements.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">INTERVENE:</span>
                      <span className="text-slate-400 leading-relaxed">
                        Built a Spring Boot microservice integrated with
                        Azure Log Analytics for activity logging, wired into
                        a Jenkins CI/CD pipeline with SonarQube analysis, and
                        covered with JUnit/Mockito unit tests.
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-accent w-24 shrink-0">RESULT:</span>
                      <span className="text-slate-100 leading-relaxed">
                        Reached 80%+ test coverage across all nine
                        microservices and automated resource provisioning
                        through Jenkins pipelines.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* The Stack */}
        {FEATURE_FLAGS.showTheStack && (
        <section className="px-4 py-16 border-b border-accent/10">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-4">
              <h2 className="text-slate-100 text-2xl font-bold tracking-tight">
                The Stack
              </h2>
              <div className="h-px flex-1 bg-accent/10" />
            </div>
            <div className="grid grid-cols-1 gap-6">
              {STACK.map(({ label, highlight, items }) => (
                <div key={label}>
                  <h4 className="font-mono text-[11px] text-slate-500 uppercase tracking-widest mb-3">
                    {label}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {items.map((item) => (
                      <span
                        key={item}
                        className={`px-3 py-1 rounded text-sm border ${
                          highlight
                            ? 'bg-accent/10 border-accent/20 text-accent'
                            : 'bg-slate-800/50 border-slate-700 text-slate-300'
                        }`}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* Writing */}
        {FEATURE_FLAGS.showWriting && (
        <section className="px-4 py-16">
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-4">
              <h2 className="text-slate-100 text-2xl font-bold tracking-tight">
                Writing
              </h2>
              <div className="h-px flex-1 bg-accent/10" />
            </div>
            <div className="flex flex-col gap-4">
              <Link
                to="/blog"
                className="group flex flex-col gap-1 p-4 rounded hover:bg-accent/5 border border-transparent hover:border-accent/20 transition-all"
              >
                <span className="font-mono text-[10px] text-accent mb-1">
                  MARCH 2024
                </span>
                <h3 className="text-slate-100 text-lg font-bold group-hover:text-accent transition-colors">
                  Handling Hallucinations in Production RAG Pipelines
                </h3>
                <p className="text-sm text-slate-400">
                  Tactical strategies for deterministic validation in
                  non-deterministic systems.
                </p>
              </Link>
              <Link
                to="/blog"
                className="group flex flex-col gap-1 p-4 rounded hover:bg-accent/5 border border-transparent hover:border-accent/20 transition-all"
              >
                <span className="font-mono text-[10px] text-accent mb-1">
                  JANUARY 2024
                </span>
                <h3 className="text-slate-100 text-lg font-bold group-hover:text-accent transition-colors">
                  Scaling Vector Databases for High-Throughput Apps
                </h3>
                <p className="text-sm text-slate-400">
                  Index optimization and memory management for large-scale
                  similarity search.
                </p>
              </Link>
            </div>
          </div>
        </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Home;
